import React, {useState, useEffect} from "react";
import {Link, useLocation} from "react-router-dom";
import {useMediaQuery} from "@mui/material";
import {
    Menu as MenuIcon,
    Home,
    AlertTriangle,
    MessageSquare,
    LogOut,
    ChevronLeft,
    User,
    Settings,
    CreditCard,
    HelpCircle,
    ClipboardList,
    MessageSquarePlus,
    Plus,
    FileText
} from "lucide-react";
import logo from "../../assets/react-logo-no-background.svg";

const Sidebar = (props) => {
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width:1000px)");
    const [collapsed, setCollapsed] = useState(isMobile);
    const [activeItem, setActiveItem] = useState("/home");
    const [hoveredItem, setHoveredItem] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);

    // Set active menu item based on current route
    useEffect(() => {
        const path = location.pathname;
        setActiveItem(path);
    }, [location]);

    // Collapse sidebar by default on mobile
    useEffect(() => {
        if (isMobile) {
            setCollapsed(true);
        }
    }, [isMobile]);

    // Handle tooltip delay
    useEffect(() => {
        let timer;
        if (hoveredItem && collapsed) {
            timer = setTimeout(() => {
                setShowTooltip(true);
            }, 500); // Show tooltip after 500ms hover
        }

        return () => {
            clearTimeout(timer);
            setShowTooltip(false);
        };
    }, [hoveredItem, collapsed]);

    const handleClick = (path) => {
        setActiveItem(path);
        if (isMobile) {
            setCollapsed(true);
        }
    };

    const logout = () => {
        localStorage.clear();
        props.refresh && props.refresh(true);
        props.fn && props.fn();
    };

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    // Styles for the sidebar with improved UX
    const styles = {
        container: {
            display: "flex",
            height: "100vh",
            position: "relative",
            // overflow: "hidden",
        },
        sidebar: {
            width: collapsed ? "70px" : "280px",
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            backgroundColor: "#111827", // Darker blue for better contrast
            color: "#f9fafb",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            zIndex: 999,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#374151 #111827"
        },
        sidebarWrapper: {
            padding: "0",
            display: "flex",
            flexDirection: "column",
            height: "100%"
        },
        headerContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            padding: "16px 12px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            // height: "70px",
            position: "sticky",
            top: 0,
            backgroundColor: "#111827",
            zIndex: 10
        },
        logoContainer: {
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            transition: "opacity 0.3s ease",
        },
        logoContainerCollapsed: {
            justifyContent: "center",
            width: "100%",
            maxWidth: "40px",
            margin: "0 auto"
        },
        logoContainerExpanded: {
            justifyContent: "flex-start",
            width: "180px"
        },
        logo: {
            maxWidth: collapsed ? "40px" : "180px",
            height: "auto",
            transition: "max-width 0.3s ease",
            objectFit: "contain"
        },
        toggleButton: {
            background: "rgba(255, 255, 255, 0.1)",
            border: "none",
            color: "#e5e7eb",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px",
            borderRadius: "6px",
            transition: "background-color 0.2s, transform 0.2s",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                transform: "scale(1.05)"
            }
        },
        sectionTitle: {
            padding: "14px 16px",
            margin: "8px 0 4px 0",
            fontSize: "12px",
            fontWeight: "600",
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "#9ca3af",
            display: collapsed ? "none" : "block"
        },
        userSection: {
            padding: "16px",
            margin: "0",
            display: collapsed ? "none" : "flex",
            flexDirection: "column",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            backgroundColor: "rgba(31, 41, 55, 0.7)",
        },
        userAvatar: {
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: "#3b82f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "12px",
            border: "3px solid rgba(59, 130, 246, 0.3)"
        },
        userName: {
            fontSize: "16px",
            fontWeight: "600",
            color: "white",
            textAlign: "center",
            marginBottom: "4px"
        },
        userProperty: {
            fontSize: "13px",
            color: "#9ca3af",
            textAlign: "center"
        },
        menuList: {
            listStyle: "none",
            padding: "4px 8px",
            margin: "0"
        },
        linkItem: {
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            display: "block"
        },
        menuItem: {
            display: "flex",
            alignItems: "center",
            padding: collapsed ? "14px 0" : "12px 16px",
            margin: "4px 0",
            justifyContent: collapsed ? "center" : "flex-start",
            cursor: "pointer",
            transition: "all 0.2s ease",
            color: "#e2e8f0",
            position: "relative",
            whiteSpace: "nowrap",
            borderRadius: "8px",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)"
            }
        },
        activeMenuItem: {
            backgroundColor: "#3b82f6",
            color: "white",
            boxShadow: "0 2px 8px rgba(59, 130, 246, 0.4)",
            "&:hover": {
                backgroundColor: "#2563eb"
            }
        },
        menuIcon: {
            marginRight: collapsed ? "0" : "12px",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: collapsed ? "auto" : "24px"
        },
        menuText: {
            fontSize: "15px",
            fontWeight: "500",
            display: collapsed ? "none" : "block",
            transition: "opacity 0.3s ease",
            opacity: collapsed ? 0 : 1
        },
        sidebarContent: {
            flexGrow: 1,
            padding: "8px 0"
        },
        sidebarBottom: {
            marginTop: "auto",
            padding: "8px 0 16px 0",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)"
        },
        logoutButton: {
            color: "#f87171",
        },
        logoutButtonActive: {
            backgroundColor: "#ef4444",
            color: "white",
            "&:hover": {
                backgroundColor: "#dc2626"
            }
        },
        mainContent: {
            marginLeft: collapsed ? "70px" : "280px",
            width: collapsed ? "calc(100% - 70px)" : "calc(100% - 280px)",
            transition: "margin-left 0.3s ease, width 0.3s ease",
            minHeight: "100vh",
            backgroundColor: "#f9fafb"
        },
        tooltip: {
            position: "absolute",
            left: "60px",
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "#111827",
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "13px",
            whiteSpace: "nowrap",
            opacity: showTooltip ? 1 : 0,
            visibility: showTooltip ? "visible" : "hidden",
            transition: "opacity 0.2s, visibility 0.2s",
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            pointerEvents: "none"
        },
        activeIndicator: {
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "4px",
            height: "60%",
            backgroundColor: "#3b82f6",
            borderRadius: "0 4px 4px 0",
            display: collapsed ? "block" : "none"
        }
    };

    // Expanded menu items with new sections
    const sections = [
        {
            title: "Dashboard",
            items: [
                {path: "/home", name: "Dashboard", icon: <Home size={20}/>},
            ]
        },
        {
            title: "Account",
            items: [
                {path: "/profile", name: "My Profile", icon: <User size={20}/>},
                {path: "/agreements", name: "Agreements", icon: <FileText size={20}/>},
                {path: "/messages", name: "Messages", icon: <MessageSquare size={20}/>},
            ]
        },

        {
            title: "Support Tickets",
            items: [
                {path: "/tickets", name: "View Tickets", icon: <ClipboardList size={20}/>},
                {path: "/tickets/new", name: "Raise Issue", icon: <AlertTriangle size={20}/>},
            ]
        },
        {
            title: "Authorization",
            items: [
                // {path: "/help", name: "Help Center", icon: <HelpCircle size={20}/>},
                {
                    path: "/",
                    name: "Logout",
                    icon: <LogOut size={20}/>,
                    onClick: logout,
                    style: styles.logoutButton,
                    activeStyle: styles.logoutButtonActive
                }
            ]
        }
    ];

    return (
        <div style={styles.container}>
            {/* Sidebar */}
            <aside style={styles.sidebar}>
                <div style={styles.sidebarWrapper}>
                    {/* Logo and Toggle Button */}
                    <div style={styles.headerContainer}>
                        <div
                            style={{
                                ...styles.logoContainer,
                                ...(collapsed ? styles.logoContainerCollapsed : styles.logoContainerExpanded)
                            }}
                        >
                            <img src={logo} alt="ITORAMA Property Management System" style={styles.logo}/>
                        </div>

                        {!collapsed && (
                            <button
                                style={{
                                    ...styles.toggleButton,
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                                        transform: "scale(1.05)"
                                    }
                                }}
                                onClick={toggleSidebar}
                                title="Collapse Sidebar"
                            >
                                <ChevronLeft size={18}/>
                            </button>
                        )}
                    </div>

                    {/* Menu Sections */}
                    <div style={styles.sidebarContent}>
                        {sections.map((section, sectionIndex) => (
                            <div key={sectionIndex}>
                                <h3 style={styles.sectionTitle}>{section.title}</h3>
                                <ul style={styles.menuList}>
                                    {section.items.map((item, itemIndex) => {
                                        const isActive = activeItem === item.path;
                                        return (
                                            <Link
                                                to={item.path}
                                                style={styles.linkItem}
                                                key={itemIndex}
                                            >
                                                <li
                                                    onMouseEnter={() => setHoveredItem(`${sectionIndex}-${itemIndex}`)}
                                                    onMouseLeave={() => setHoveredItem(null)}
                                                    onClick={() => {
                                                        handleClick(item.path);
                                                        item.onClick && item.onClick();
                                                    }}
                                                    style={{
                                                        ...styles.menuItem,
                                                        ...(isActive ? {
                                                            ...styles.activeMenuItem,
                                                            ...(item.activeStyle || {})
                                                        } : {}),
                                                        ...(item.style || {})
                                                    }}
                                                >
                                                    {isActive && collapsed && <div style={styles.activeIndicator}/>}

                                                    <span style={styles.menuIcon}>
                            {item.icon}
                          </span>

                                                    <span style={styles.menuText}>{item.name}</span>

                                                    {/* Tooltip for collapsed mode */}
                                                    {collapsed && hoveredItem === `${sectionIndex}-${itemIndex}` && (
                                                        <div style={styles.tooltip}>{item.name}</div>
                                                    )}
                                                </li>
                                            </Link>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Collapse/Expand Button at Bottom for Mobile */}
                    {collapsed && (
                        <div style={{
                            padding: "16px 12px",
                            textAlign: "center",
                            borderTop: "1px solid rgba(255, 255, 255, 0.1)"
                        }}>
                            <button
                                style={{
                                    ...styles.toggleButton,
                                    width: "40px",
                                    height: "40px"
                                }}
                                onClick={toggleSidebar}
                                title="Expand Sidebar"
                            >
                                <MenuIcon size={20}/>
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <main style={styles.mainContent}>
                {props.children}
            </main>

            <style jsx="true">{`
                /* Custom scrollbar styles */
                *::-webkit-scrollbar {
                    width: 6px;
                }

                *::-webkit-scrollbar-track {
                    background: #1f2937;
                }

                *::-webkit-scrollbar-thumb {
                    background-color: #4b5563;
                    border-radius: 20px;
                }

                /* Hover effect for menu items */
                .sidebar-menu-item:hover {
                    background-color: rgba(255, 255, 255, 0.1);
                }

                .sidebar-menu-item.active {
                    background-color: #3b82f6;
                }

                /* Animation for sidebar toggle */
                @keyframes slideIn {
                    from {
                        transform: translateX(-100%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default Sidebar;